import type { IconName } from '../chrome/icons';

/*
 * The picture and clip tools (Tarek, 14 Oct, Canva's Edit panel as the reference: "add these tools
 * please, use the AI models that we have … we need vectorize … layer split"). Names are our own —
 * never Canva's "Magic ___" marks (IP map) — and say what the tool does.
 *
 * Every AI tool runs through the run Generate already uses (`nodes.standalone.*`: one credit hold,
 * the result filed in My Stuff, the glow and the zoom pill's bar while it works). Models are the
 * ones Flow, Director and Brands already run (catalogue ids as ai-back knows them).
 */

export type ToolId =
  | 'adjust' | 'blur' | 'upscale' | 'remove-background' | 'erase' | 'focus' | 'retouch'
  | 'edit' | 'new-background' | 'expand' | 'animate'
  | 'split' | 'split-text' | 'cut-out' | 'vectorize'
  | 'clip-remove-background' | 'clip-upscale' | 'clip-edit';

/** How a result lands (`landResult.ts`; vectors through the canvas). */
export type Landing = 'replace' | 'expand' | 'layers' | 'document' | 'vector' | 'cut-out' | 'focus' | 'clip';

export interface ToolDef {
  id: ToolId;
  label: string;
  /** One line under the tool's name in its own view. */
  line: string;
  group: 'enhance' | 'reimagine' | 'layers' | 'clip';
  icon: IconName;
  /** Local tools change the layer's effects; nothing is generated or charged. */
  local?: boolean;
  model?: string;
  kind?: 'image' | 'video';
  land?: Landing;
  /** What the tool asks for in its view. */
  asks?: ('prompt' | 'prompt-optional' | 'area' | 'ratio' | 'count')[];
  /**
   * The picture is scaled to fit before it is sent: some models refuse (or bill a tier up) past
   * a size. Bria's ad delayer — layers with live text — takes at most 2048 px a side and 10 MB
   * (Bria Ads API reference, "Limits and retention"); Seedream needs 512 × 512 at least.
   */
  maxEdge?: number;
  maxBytes?: number;
  /** Fixed words a tool sends when the person writes none (some endpoints refuse an empty prompt). */
  words?: string;
  /** Starting points for a tool that takes words: a press puts one in the field. */
  examples?: string[];
  /** One short line under the button, only where the result lands somewhere else. */
  caption?: string;
  /** Extra settings the endpoint reads. */
  inputs?: Record<string, unknown>;
  /**
   * A clip tool whose model takes clips of this many seconds only, and is told the clip's own
   * length (whole seconds) so the hold and the price are the clip's, not the model's longest.
   */
  clipSeconds?: { min: number; max: number };
}

/**
 * A brushed area is sent as the picture itself with the area highlighted in translucent magenta
 * (run.ts `highlighted`), to Nano Banana 2's edit. Tried first and refused by what they made:
 * FLUX Fill with a mask drew a face back into a face (it paints what the words describe), and Nano
 * Banana with the mask as a second picture painted the mask's white strokes into the result.
 */
export const MASK_MODEL = 'nano-banana-2';
/**
 * Background removal: Ideogram, chosen 15 Oct from the four in the catalogue on one portrait
 * (curly hair, beard, ear): Pixelcut (the first pick) cut the ear off with a jagged edge; Bria
 * RMBG 2.0 answers at 1024 px, blurred back up; BEN 2 left a soft halo and took 51 s (~92
 * credits at 1920²); Ideogram kept the curls, ear and beard clean at full size — 19 s, 10 credits.
 */
export const CUTOUT_MODEL = 'ideogram-remove-background';
/**
 * One picture back. nodes-back holds a per-picture model's credits ×4 unless `num_layers` says how
 * many come back (pricing-units), so Upscale was quoted 160 for a 40-credit run.
 */
const ONE = { num_layers: 1 };
/** Upscale to a size: Crystal takes any factor, Flow's Upscale runs it (upscaleSizes.ts); `model` below keeps a transparent picture's transparency. */
export const UPSCALE_MODEL = 'philz1337x/crystal-upscaler';
const MASK_WORDS = 'The area highlighted in translucent magenta marks exactly what to change. Change only that area; keep everything outside it exactly as it is, pixel for pixel. The result must show no magenta highlight at all.';
const ERASE_WORDS = 'Remove whatever is under the highlight completely and fill that area with the background around it — same light, texture and perspective — as if it was never there.';
/** Focus's strength: the background blur's radius as a share of the picture's long side (focus.ts). */
export const FOCUS_BLUR_BY: Record<'light' | 'natural' | 'strong', number> = { light: 0.01, natural: 0.018, strong: 0.03 };
/** Retouch's strength (Light · Natural · Strong): how far the skin is evened; faces never change. */
export type Strength = 'light' | 'natural' | 'strong';
const KEEP = 'Do not change faces, features, expression, hair, clothes, framing or anything else.';
export const RETOUCH: Record<Strength, string> = {
  light: `Lightly retouch the people in this photo: soften only the most visible blemishes, keep every bit of skin texture and detail. ${KEEP}`,
  natural: `Retouch the people in this photo naturally: even out skin tone, soften blemishes and under-eye shadows, keep skin texture. ${KEEP}`,
  strong: `Retouch the people in this photo thoroughly: even out skin tone, remove blemishes and under-eye shadows, smooth the skin while it still looks real. ${KEEP}`,
};

export const TOOLS: ToolDef[] = [
  // Enhance
  { id: 'adjust', label: 'Adjust', line: 'Light, colour and detail, changed live. The picture underneath is kept as it is.', group: 'enhance', icon: 'adjust', local: true },
  { id: 'upscale', label: 'Upscale', line: 'Sharper detail at 1K, 2K or 4K. A transparent picture doubles and stays transparent.', group: 'enhance', icon: 'zoom-in', model: 'bria-increase-resolution', kind: 'image', land: 'replace', words: 'upscale image', inputs: ONE },
  { id: 'remove-background', label: 'Remove Background', line: 'Keeps the subject on a transparent background.', group: 'enhance', icon: 'scissors', model: CUTOUT_MODEL, kind: 'image', land: 'replace', words: 'remove background', inputs: ONE },
  { id: 'erase', label: 'Erase Object', line: 'Brush over anything you want gone; the background is filled in.', group: 'enhance', icon: 'eraser', model: MASK_MODEL, kind: 'image', land: 'replace', asks: ['area'], words: ERASE_WORDS },
  { id: 'blur', label: 'Blur', line: 'Softens the whole picture, live.', group: 'enhance', icon: 'smooth', local: true },
  { id: 'focus', label: 'Focus', line: 'Blurs the background behind the subject, like a wide-open lens.', group: 'enhance', icon: 'select-object', model: CUTOUT_MODEL, kind: 'image', land: 'focus', words: 'remove background', inputs: ONE },
  { id: 'retouch', label: 'Retouch', line: 'Evens skin and softens blemishes. Faces and features stay the same.', group: 'enhance', icon: 'person', model: 'nano-banana-2.1', kind: 'image', land: 'replace', words: RETOUCH.natural },
  // Reimagine
  { id: 'edit', label: 'Edit with Words', line: 'Say what to change. Brush an area to change only that.', group: 'reimagine', icon: 'generate-sparkles', model: 'nano-banana-2.1', kind: 'image', land: 'replace', asks: ['prompt', 'area'],
    examples: ['Make it night', 'Golden hour light', 'Take out the people in the back'] },
  { id: 'new-background', label: 'New Background', line: 'Keeps the subject and puts it somewhere new.', group: 'reimagine', icon: 'landscape', model: 'nano-banana-2.1', kind: 'image', land: 'replace', asks: ['prompt'],
    examples: ['A plain white studio backdrop', 'A marble kitchen counter', 'A beach at sunset', 'A soft pastel gradient'] },
  { id: 'expand', label: 'Expand', line: 'Draws more of the scene around the picture, to a new shape.', group: 'reimagine', icon: 'expand', model: 'luma-reframe-image', kind: 'image', land: 'expand', asks: ['ratio'], words: 'extend the scene naturally', inputs: ONE },
  { id: 'animate', label: 'Image to Video', line: 'Opens Generate with this picture as the first frame.', group: 'reimagine', icon: 'format-video' },
  // Layers
  { id: 'split', label: 'Split into Layers', line: 'Separates the picture into layers you can move, on a new artboard.', group: 'layers', icon: 'layers', model: 'qwen-image-layered', kind: 'image', land: 'layers', asks: ['count'], words: 'separate into layers', caption: 'New artboard beside this one' },
  { id: 'split-text', label: 'Layers with Live Text', line: 'Pictures, shapes and editable text from a flat design, on a new artboard.', group: 'layers', icon: 'text', model: 'bria/ad-delayer', kind: 'image', land: 'document', words: 'split into layers', maxEdge: 2048, maxBytes: 10 * 1024 * 1024, inputs: ONE, caption: 'New artboard · text stays editable' },
  { id: 'cut-out', label: 'Cut Out Subject', line: 'Lifts the subject onto its own layer above the picture.', group: 'layers', icon: 'select-object', model: CUTOUT_MODEL, kind: 'image', land: 'cut-out', words: 'remove background', inputs: ONE, caption: 'A new layer above the picture' },
  { id: 'vectorize', label: 'Vectorize', line: 'Traces the picture into editable vector shapes.', group: 'layers', icon: 'format-vector', model: 'image2svg', kind: 'image', land: 'vector', words: 'vectorize', maxEdge: 2048, inputs: ONE },
  // Clips
  { id: 'clip-remove-background', label: 'Remove Background', line: 'Keeps the subject of the clip on a transparent background.', group: 'clip', icon: 'scissors', model: 'veed-video-background-removal-fast', kind: 'video', land: 'clip', words: 'remove background' },
  { id: 'clip-upscale', label: 'Upscale', line: 'A sharper, larger clip with the same motion.', group: 'clip', icon: 'zoom-in', model: 'topaz-video-precision-upscale', kind: 'video', land: 'clip', words: 'upscale video', inputs: { H264_output: true } },
  { id: 'clip-edit', label: 'Edit with Words', line: 'Restyle or replace what is in the shot; the motion is kept.', group: 'clip', icon: 'generate-sparkles', model: 'wan-2.7-edit', kind: 'video', land: 'clip', asks: ['prompt'],
    // fal's edit-video takes 2–10 s and bills the output's seconds; unset it was held at Wan's 15.
    clipSeconds: { min: 2, max: 10 } },
];

export const toolById = (id: ToolId) => TOOLS.find(tool => tool.id === id)!;

/** Plain verbs over the groups (audit, 15 Oct): what each does to the picture, not a mood. */
export const GROUPS: { id: ToolDef['group']; label: string }[] = [
  { id: 'enhance', label: 'Clean up' },
  { id: 'reimagine', label: 'Change' },
  { id: 'layers', label: 'Take apart' },
];

/** Expand's shapes: the common post and screen shapes, the picture's own first. */
export const EXPAND_RATIOS = ['1:1', '4:5', '3:4', '2:3', '9:16', '16:9', '3:2', '4:3', '21:9'] as const;

/** The words a run sends: what was typed, framed for the tool, or the tool's own. */
export function toolPrompt(tool: ToolDef, typed: string, area: boolean, strength?: Strength): string {
  const said = typed.trim();
  if (tool.id === 'retouch') return RETOUCH[strength ?? 'natural'];
  if (tool.id === 'new-background') return `Keep the main subject exactly as it is — same pose, size, position, lighting on it and every detail — and replace everything behind it with: ${said}. Match the light and shadows to the new place.`;
  if (area) return `${MASK_WORDS} ${tool.id === 'erase' ? ERASE_WORDS : said}`;
  if (tool.id === 'edit') return `${said}. Keep everything you are not asked to change exactly as it is.`;
  return said || tool.words || '';
}
